// @ts-strict-ignore
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import {StateController} from 'panel';

import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {getPublicDashboardContentStats} from 'common/report/dashboards/tracking';
import {SharingTrackEvents, SharingTrackProperties} from 'common/widgets/sharing/tracking';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {reporter} from 'common/report/error-reporting';
import {sanitizeBookmarkParams as sanitizeFunnelsBookmarkParams} from 'common/report/funnels/util';
import {
  BadLayoutRequestErrors,
  ContentType,
  Dashboard,
  ExperimentCard,
  HeatmapCard,
  PlaylistCard,
} from 'common/report/dashboards/types';
import {ResponseError} from 'common/util/shared-client';
import {LocalizationStore} from 'common/i18n/store';

import {getContentsInGridLayout} from '../util/dashboards/grid-layout';
import {getCardIdFromContent} from '../util/dashboards/utils';
import {Store} from '../stores/store';
import {DashboardsMap} from '../stores/dashboards';
import type {DashboardSelectorController} from './dashboard-selector-controller';
import {DashboardsModelClient, QuerySingleDashboardType} from './types';

export const INVALID_BOOKMARK_ERROR = `boards-invalid-bookmark`;
const INVALID_BOOKMARK = {params: {}, error: INVALID_BOOKMARK_ERROR, name: ``, description: ``};

export const setCardsFromDashboardLayout = (model: Dashboard) => {
  if (isEmpty(model.contents) || isEmpty(model.layout)) {
    model.cards = [];
    model.card_order = [];
    return;
  }

  const {
    [ContentType.Report]: bookmarkHash,
    [ContentType.ReportLink]: dashboardCardsHash,
    [ContentType.Text]: textCardsHash,
    [ContentType.Media]: mediaCardsHash,
    [ContentType.Dashboard]: nestedDashboardsHash,
    [ContentType.Benchmark]: benchmarkCardsHash,
    [ContentType.Heatmap]: heatmapCardsHash,
    [ContentType.HeatmapCollection]: heatmapCollectionCardsHash,
    [ContentType.Playlist]: playlistCardsHash,
    [ContentType.Experiment]: experimentCardsHash,
  } = model.contents;

  const cardOrder = [];
  const cards = [];
  const contents = getContentsInGridLayout(model.layout);
  contents.forEach((content) => {
    const {contentId, contentType} = content;
    let card;
    const cardId = getCardIdFromContent(contentId, contentType);
    if (contentType === ContentType.Report) {
      card = getReportCard(bookmarkHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.ReportLink) {
      card = getReportLinkCard(dashboardCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Text) {
      card = getTextCard(textCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Media) {
      card = getMediaCard(mediaCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Dashboard) {
      card = getNestedDashboardCard(nestedDashboardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Benchmark) {
      card = getBenchmarkCard(benchmarkCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Heatmap) {
      card = getHeatMapCard(heatmapCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.HeatmapCollection) {
      card = getHeatMapCollectionCard(heatmapCollectionCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Playlist) {
      card = getPlaylistCard(playlistCardsHash, contentId, null, null, cardId);
    } else if (contentType === ContentType.Experiment) {
      card = getExperimentCard(experimentCardsHash, contentId, null, null, cardId);
    }

    cards.push(card);
    cardOrder.push(card.id);
  });

  model.cards = cards;
  model.card_order = cardOrder;

  function getHeatMapCard(heatmapHash, heatmapId, cardWidth, cardHeight, cardId): HeatmapCard {
    const heatMap = heatmapHash[heatmapId] || {};

    return {
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      content_id: heatMap.id,
      type: ContentType.Heatmap,
      title: heatMap.title || ``,
    };
  }

  function getHeatMapCollectionCard(
    heatmapCollectionHash,
    heatmapCollectionId,
    cardWidth,
    cardHeight,
    cardId,
  ): HeatmapCard {
    const heatMapCollection = heatmapCollectionHash[heatmapCollectionId] || {};

    return {
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      content_id: heatMapCollection.id,
      type: ContentType.HeatmapCollection,
      title: heatMapCollection.name || ``,
    };
  }

  function getPlaylistCard(playlistHash, playlistId, cardWidth, cardHeight, cardId): PlaylistCard {
    const playlist = playlistHash[playlistId] || {};

    return {
      id: cardId,
      name: playlist.name,
      width: cardWidth,
      height: cardHeight,
      content_id: playlist.id,
      type: ContentType.Playlist,
    };
  }

  function getExperimentCard(experimentHash, experimentCardId, cardWidth, cardHeight, cardId): ExperimentCard {
    const experimentCard = experimentHash[experimentCardId] || {};

    return {
      id: cardId,
      name: experimentCard.name,
      width: cardWidth,
      height: cardHeight,
      content_id: experimentCard.id,
      type: ContentType.Experiment,
      experiment_id: experimentCard.experiment_id ?? null,
      display_options: experimentCard.display_options,
    };
  }

  function getBenchmarkCard(benchmarkHash, benchmarkId, cardWidth, cardHeight, cardId) {
    const benchmark = benchmarkHash[benchmarkId] || {};

    return {
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      content_id: benchmarkId,
      benchmark_type: benchmark.benchmark_type,
      industry: benchmark.industry,
      bookmark: benchmark.bookmark,
      type: ContentType.Benchmark,
      ...(!isEmpty(benchmark.bookmark.metadata) ? {bookmark_metadata: benchmark.bookmark.metadata} : {}),
    };
  }

  function getReportLinkCard(dashboardCardHashes, reportLinkId, cardWidth, cardHeight, cardId) {
    const dashboardCard = dashboardCardHashes[reportLinkId] || {};
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {bookmark_metadata, chart_options, title} = dashboardCard;
    let {bookmark} = dashboardCard;
    if (!bookmark) {
      reporter.error(`Card ${cardId} doesn't have a valid bookmark`);
      bookmark = INVALID_BOOKMARK;
    }
    return {
      id: cardId,
      title, // TODO use bookmark title when boards is GA
      description: bookmark?.description,
      width: cardWidth,
      height: cardHeight,
      content_id: reportLinkId,
      bookmark,
      // eslint-disable-next-line camelcase
      bookmark_metadata,
      // eslint-disable-next-line camelcase
      ...(!isEmpty(chart_options) ? {chart_options} : {}),
      linked_dashboard_card_id: reportLinkId,
      type: ContentType.ReportLink,
    };
  }

  function getTextCard(textCardHashes, textId, cardWidth, cardHeight, cardId) {
    const textCard = textCardHashes[textId] || {};
    return {
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      type: ContentType.Text,
      markdown: textCard.markdown,
      text_card_id: textId,
      content_id: textId,
    };
  }

  function getMediaCard(mediaCardHashes, mediaCardId, cardWidth, cardHeight, cardId) {
    const mediaCard = mediaCardHashes[mediaCardId] || {};
    return {
      // mediaCard.id is the same as mediaCardId
      ...mediaCard,
      id: cardId,
      width: cardWidth,
      height: cardHeight,
      type: ContentType.Media,
      media_card_id: mediaCardId,
      content_id: mediaCardId,
    };
  }
};

function getNestedDashboardCard(nestedDashboardCardsHash, nestedDashboardCardId, cardWidth, cardHeight, cardId) {
  const nestedDashboardCard = nestedDashboardCardsHash[nestedDashboardCardId] || {};
  return {
    ...nestedDashboardCard,
    id: cardId,
    width: cardWidth,
    height: cardHeight,
    type: ContentType.Dashboard,
    dashboard_id: nestedDashboardCardId,
    content_id: nestedDashboardCardId,
  };
}

export function getReportCard(bookmarkHash, bookmarkId, cardWidth, cardHeight, cardId) {
  let bookmark = bookmarkHash[bookmarkId];
  // Only for opt-in/out purposes
  if (!bookmark) {
    reporter.error(`Card ${cardId} doesn't have a valid bookmark`);
    bookmark = INVALID_BOOKMARK;
  }

  return {
    id: cardId,
    title: bookmark.name,
    description: bookmark.description,
    width: cardWidth,
    height: cardHeight,
    content_id: bookmarkId,
    bookmark,
    type: ContentType.Report,
    ...(isEmpty(bookmark.metadata) ? {} : {bookmark_metadata: bookmark.metadata}),
  };
}

function sanitizeDashboardModel(dashboard, i18n: LocalizationStore) {
  function sanitizeDashboardCards(dashboardCards) {
    return dashboardCards.map((dashboardCard) => {
      const bookmark = dashboardCard.bookmark;
      if (bookmark?.type === BookmarkType.Funnels) {
        const sanitizedParams = sanitizeFunnelsBookmarkParams(bookmark.params, i18n);
        if (!isEmpty(sanitizedParams)) {
          const updatedBookmark = {
            ...bookmark,
            params: {
              ...bookmark.params,
              ...sanitizedParams,
            },
          };
          return {...dashboardCard, bookmark: updatedBookmark};
        }
      }
      return dashboardCard;
    });
  }

  const updatedParams: Partial<Dashboard> = {};
  if (dashboard.cards) {
    updatedParams.cards = sanitizeDashboardCards(dashboard.cards);
  }

  return {
    ...dashboard,
    ...updatedParams,
  };
}

type DashboardModelControllerState = {
  dashboard: Dashboard;
  isLoaded: boolean;
  error: ResponseError;
  modified: string;
};

export class DashboardModelController<
  CustomClientType extends DashboardsModelClient,
> extends StateController<DashboardModelControllerState> {
  declare parentController: DashboardSelectorController<CustomClientType>;
  declare client: CustomClientType;
  declare dashboardStore: Store<DashboardsMap>;
  declare _loadModelPromises: QuerySingleDashboardType;
  declare i18n: LocalizationStore;

  constructor({
    model,
    selectorController,
    dashboardsClient,
    dashboardStore,
    i18n,
  }: {
    model: Partial<Dashboard>;
    selectorController: DashboardSelectorController<CustomClientType>;
    dashboardsClient: CustomClientType;
    dashboardStore: Store<DashboardsMap>;
    i18n: LocalizationStore;
  }) {
    super(model as any);
    this.parentController = selectorController;
    this.client = dashboardsClient;

    // note: will be null if this is a public URL, stores don't work in unauthenticated environments
    this.dashboardStore = dashboardStore;
    this.i18n = i18n;

    this._update({dashboard: {...this.state.dashboard, ...model}});
  }

  get defaultState(): DashboardModelControllerState {
    return {
      dashboard: {
        id: null,
        title: null,
        description: null,
        creator_id: null,
        creator_name: null,
        creator_email: null,
        contents: null,
        card_order: null,
        cards: null,
        time_filter: null,
        filters: null,
        breakdowns: [],
        layout: null,
        // permissions
        can_view: false,
        can_update_basic: true,
        can_share: false,
        is_superadmin: false,
        allow_staff_override: false,

        is_draft: false,
        is_favorited: false,
        pinned_date: null,
        num_active_public_links: 0,
        is_shared_with_project: false,
        parent_dashboard_id: null,
      },
      isLoaded: false,
      error: null,
      modified: null,
    };
  }

  updateModel(dashboardProps: Partial<Dashboard>) {
    const updatedDashboard = {...this.state.dashboard, ...dashboardProps};
    this._update({dashboard: updatedDashboard});
    this.parentController._updateCachedDashboard(this.state.dashboard.id, updatedDashboard);
  }

  getModel(): Dashboard {
    const dashboard = this.state.dashboard;
    const model: Dashboard = {...this.state.dashboard};
    if (!isEmpty(dashboard.time_filter) && Object.keys(dashboard.time_filter).length > 0) {
      const timeFilter = {...dashboard.time_filter};
      timeFilter.dateRange = new DateRange(timeFilter.dateRange);
      model.time_filter = timeFilter;
    } else {
      model.time_filter = {};
    }

    if (!dashboard.filters) {
      model.filters = [];
    }

    return model;
  }

  getLoaded() {
    return this.state.isLoaded;
  }

  // redundant method that is more consistent with out app verbiage
  isLoading() {
    return !this.state.isLoaded;
  }

  getError() {
    return this.state.error;
  }

  // TODO @shayan or @gonzalo: the networkOnly param is a workaround for the grid project. It is used just after we update a bookmark with the bookmark API, to refresh the base api cache
  async loadModel({networkOnly} = {networkOnly: false}) {
    this._update({isLoaded: false, error: null});

    const queryParams = {
      projectId: this.parentController.projectId,
      dashboardID: this.state.dashboard.id,
      workspaceId: this.parentController.workspaceId,
    };

    // prevent redundant network requests
    if (!this._loadModelPromises) {
      this._loadModelPromises = this.client.querySingle(queryParams, {clearCache: true});

      if (networkOnly && `network` in this._loadModelPromises) {
        this._loadModelPromises.cached = Promise.resolve(null);
      }
      // baseClient returns an object with both network and cache promises, while apiClient returns
      // a single promise. This will ensure both types of clients behave the same
      (Object.prototype.hasOwnProperty.call(this._loadModelPromises, `network`)
        ? Object.values(this._loadModelPromises)
        : [this._loadModelPromises]
      ).forEach((promise) =>
        promise
          .then((dashboard: Dashboard) => {
            if (dashboard) {
              const state: Partial<DashboardModelControllerState> = {
                dashboard: {...this.state.dashboard, ...sanitizeDashboardModel(dashboard, this.i18n)},
                isLoaded: true,
                error: null,
              };
              setCardsFromDashboardLayout(state.dashboard);
              this._update(state);

              // note: dashboardStore will be null if this is a public URL, stores don't work in unauthenticated environments
              if (this.dashboardStore) {
                const storeKey = this.state.dashboard.id.toString();
                // It's hacky but dashboardStore is manually updated here because Store fetching is hard to adapt to this model controller.
                // Why use a Store at all? To share/subscribe values with other Stores and start the ball rolling on a Store replacement for model controllers.
                // TODO: ideally DashboardModelController and DashboardSelectorController should be more thoroughly replaced by Stores or Stores v2 (ie. Resources)
                this.dashboardStore.update({[storeKey]: dashboard}, storeKey);
              }
            }
          })
          .catch((error) => {
            reporter.error(error);
            this._update({isLoaded: false, error});
          }),
      );
    }

    // fallback to network if cached value doesn't exist
    let cached;

    if (`cached` in this._loadModelPromises) {
      cached = await this._loadModelPromises.cached;
    }

    if (!cached && `network` in this._loadModelPromises) {
      await this._loadModelPromises.network;
    }

    Promise.all(Object.values(this._loadModelPromises))
      .catch((err) => {
        throw err;
      })
      .finally(() => (this._loadModelPromises = null));
  }

  async updateDashboard({fields = {}, updateModel = true} = {}) {
    const originalModel = this.getModel();
    try {
      if (updateModel) {
        // layout and contents not included
        const newProposedModel = {
          ...pick(fields, [`filters`, `breakdowns`, `time_filter`, `title`, `description`]),
        };

        this.updateModel(newProposedModel);
      }

      let results = originalModel;
      if (Object.keys(fields).length !== 0) {
        results = await this.client.updateDashboardFields?.({
          projectId: this.parentController.projectId,
          workspaceId: this.parentController.workspaceId,
          dashboardID: this.state.dashboard.id,
          fields,
        });
      }
      setCardsFromDashboardLayout(results);

      if (updateModel) {
        this.updateModel(results);
      }

      if (this.dashboardStore) {
        const storeKey = this.state.dashboard.id.toString();
        this.dashboardStore.update({[storeKey]: results}, storeKey);
      }

      if (results.contents && (results.num_active_public_links ?? 0) > 0) {
        const {numHyperlinks, numContent} = getPublicDashboardContentStats(results.contents);

        mixpanel.track(SharingTrackEvents.PublicDashboardContentChanged, {
          [SharingTrackProperties.NumHyperlinks]: numHyperlinks,
          [SharingTrackProperties.NumContent]: numContent,
          [SharingTrackProperties.DashboardId]: results.id,
        });
      }

      return results;
    } catch (error) {
      if (Object.values(BadLayoutRequestErrors).includes(error.response?.error?.type)) {
        const latestDashboard = error.response.error.dashboard;
        setCardsFromDashboardLayout(latestDashboard);
        this.updateModel(latestDashboard);
      } else {
        this.updateModel(originalModel);
      }
      throw error;
    }
  }

  // wrapper for base class StateController
  _update(props: Partial<DashboardModelControllerState>) {
    super._update(props);
  }
}
